import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import Joi from "joi";
import _ from "lodash";
import moment from "moment";
import http from "../services/http";
import Input from "./common/input";
import Select from "./common/select";
import Checkbox from "./common/checkbox";
import Chart from "./profileChart";
import UserContext from "../context/userContext";

const UserForm = ({ match, history }) => {
  const [user] = useContext(UserContext);
  const [access, setAccess] = useState([]);
  const [profile, setProfile] = useState({
    FIRST_NAME: "",
    LAST_NAME: "",
    EMAIL: "",
    PHONE: "",
    READ_ACCESS: 0,
    WRITE_ACCESS: 0,
    ACCESS_BA: false,
    ACCESS_BU: false,
    ACCESS_CA: false,
    ACCESS_NE: false,
    ACCESS_WI: false,
    ACCESS_CO: false,
  });
  const [errors, setErrors] = useState({});
  const [screenLog, setScreenLog] = useState([]);

  const screenFromDate = moment().subtract(7, "days").format("YYYY-MM-DD");
  const screenToDate = moment().format("YYYY-MM-DD");

  const postSchema = {
    USERNAME: Joi.string(),
    FIRST_NAME: Joi.string().min(2).required().label("First Name"),
    LAST_NAME: Joi.string().min(2).required().label("Last Name"),
    EMAIL: Joi.string().email({ tlds: false }).label("Email").allow(""),
    PHONE: Joi.string()
      .trim()
      .regex(/^[0-9 ]{7,11}/)
      .allow(""),
    READ_ACCESS: Joi.number().min(0).max(5),
    WRITE_ACCESS: Joi.number().min(0).max(5),
    ACCESS_BA: Joi.boolean().required(),
    ACCESS_BU: Joi.boolean().required(),
    ACCESS_CA: Joi.boolean().required(),
    ACCESS_NE: Joi.boolean().required(),
    ACCESS_WI: Joi.boolean().required(),
    ACCESS_CO: Joi.boolean().required(),
  };

  const putSchema = {
    USERNAME: Joi.string(),
    FIRST_NAME: Joi.string().min(2).required().label("First Name"),
    LAST_NAME: Joi.string().min(2).required().label("Last Name"),
    EMAIL: Joi.string().email({ tlds: false }).label("Email").allow(""),
    PHONE: Joi.string()
      .trim()
      .regex(/^[0-9 ]{7,11}/)
      .allow(""),
    READ_ACCESS: Joi.number().min(0).max(5),
    WRITE_ACCESS: Joi.number().min(0).max(5),
    ACCESS_BA: Joi.boolean().required(),
    ACCESS_BU: Joi.boolean().required(),
    ACCESS_CA: Joi.boolean().required(),
    ACCESS_NE: Joi.boolean().required(),
    ACCESS_WI: Joi.boolean().required(),
    ACCESS_CO: Joi.boolean().required(),
    VIEW_DATA_SCREENS: Joi.boolean().required(),
    VIEW_DASHBOARDS: Joi.boolean().required(),
    REPORTS: Joi.boolean().required(),
    MAINTAIN_REPORTS: Joi.boolean().required(),
    DATA_GRABBER: Joi.boolean().required(),
    MAINTAIN_DATA_SCREENS: Joi.boolean().required(),
    MAINTAIN_DASHBOARDS: Joi.boolean().required(),
    MAINTAIN_GRABBERS: Joi.boolean().required(),
    PARAMETERS: Joi.boolean().required(),
    HISTORIC_DATA: Joi.boolean().required(),
    CONSTANTS: Joi.boolean().required(),
    PRODUCTION_COMMENTS: Joi.boolean().required(),
    OPERATIONS: Joi.boolean().required(),
    LOOKUPS: Joi.boolean().required(),
    USERS: Joi.boolean().required(),
    LIVE_DATA: Joi.boolean().required(),
    RECALCULATE: Joi.boolean().required(),
    VIEW_CCF: Joi.boolean().optional(),
    MAINTAIN_CCF: Joi.boolean().optional(),
    LAST_UPDATED_BY: Joi.any(),
    LAST_UPDATED_DTS: Joi.any(),
    ACTIVE: Joi.boolean()
 };

  useEffect(() => {
    async function getAccess() {
      try {
        const { data } = await http.get(`/data-access`);
        setAccess(data);
      }
      catch {
        //ignore error
      }
    }

    getAccess();
  }, []);

  useEffect(() => {
    if (user && match.params.id === "new") {
      setProfile((p) => {
        return { ...p, [`ACCESS_${user.SITE}`]: true };
      });
    }
  }, [user, match.params.id]);

  useEffect(() => {
    async function getUser() {
      try {
        const { data } = await http.get(`/user/${match.params.id}`);
        setProfile(clearNull(data));
      }
      catch {
        //ignore error
      }
    }

    async function getScreenLog() {
      try {
        const { data } = await http.get(`/data-screen-log/${user.SITE}/${match.params.id}/${screenFromDate}/${screenToDate}`);
        setScreenLog(data);
      }
      catch {
        //ignore error
      }
    }

    if (user && match.params.id !== "new") {
      getUser();
      getScreenLog();
    }
  }, [user, match.params.id, screenFromDate, screenToDate]);

  function clearNull(data) {
    for (var prop in data) {
      if (data[prop] === null) data[prop] = "";
    }
    return data;
  }

  function handleChange({ currentTarget: input }) {
    const newErrors = { ...errors };
    const schema = match.params.id === "new" ? postSchema : putSchema;
    const errorMessage = validateProperty(input, schema);
    if (errorMessage) newErrors[input.name] = errorMessage;
    else delete newErrors[input.name];
    setErrors(newErrors);

    const info = { ...profile };
    const value = input.type === "checkbox" ? input.checked : input.value;
    info[input.name] = value;
    setProfile(info);
  }

  function validateProperty({ name, value }, schema) {
    const obj = { [name]: value };
    const propSchema = Joi.object({ [name]: schema[name] });
    const { error } = propSchema.validate(obj);
    return error ? error.details[0].message : null;
  }

  function validate() {
    const schema = match.params.id === "new" ? postSchema : putSchema;
    const options = { abortEarly: false };
    const { error } = Joi.object(schema).validate(profile, options);
    if (!error) return null;

    const errors = {};
    for (let item of error.details) errors[item.path[0]] = item.message;
    return errors;
  }

  async function handleUpdate(e) {
    e.preventDefault();
    // VALIDATE
    if (match.params.id === "new") {
      const postProfile = _.pick(profile, [
        "FIRST_NAME",
        "LAST_NAME",
        "EMAIL",
        "READ_ACCESS",
        "WRITE_ACCESS",
        "ACCESS_BA",
        "ACCESS_BU",
        "ACCESS_CA",
        "ACCESS_NE",
        "ACCESS_WI",
        "ACCESS_CO",
      ]);
      try {
        await http.post(`/user`, postProfile);
        history.push("/users");
      } catch (ex) {
        if (ex.response.status <= 400 || ex.response.status > 500) {
          console.log(ex.response);
          toast.error(ex.response.data);
        }
      }
    } else {
      try {
        await http.put(`/user/${match.params.id}`, profile);
        toast.success("User Updated");
      } catch (ex) {
        if (ex.response) {
          toast.error(ex.response.data);
        }
      }
    }
  }

  async function handlePasswordReset() {
    try {
      await http.post(`/user-reset/user/${profile.USERNAME}`);
      toast.success("Email Sent");
    } catch (ex) {
      if (ex.response) {
        toast.error(ex.response.data);
      }
    }
  }

  return (
    <div className="col mt-4">
      <div className="container">
        <form>
          <h5>{profile && profile.USERNAME}</h5>
          <Input
            name="FIRST_NAME"
            label="First Name"
            error={errors.FIRST_NAME}
            placeholder="First Name"
            value={profile.FIRST_NAME}
            onChange={handleChange}
          />
          <Input
            name="LAST_NAME"
            label="Surname"
            error={errors.LAST_NAME}
            placeholder="Surname"
            value={profile.LAST_NAME}
            onChange={handleChange}
          />
          <Input
            name="EMAIL"
            label="Email"
            error={errors.EMAIL}
            placeholder="Email Address"
            value={profile.EMAIL}
            onChange={handleChange}
          />
          <div className="row justify-content-center">
            <div className="col-4">
              <Select
                name="READ_ACCESS"
                label="Read Access"
                placeholder="Access"
                value={profile.READ_ACCESS}
                options={access}
                onChange={handleChange}
                error={errors.READ_ACCESS}
                listKey="ID"
                listName="DESCRIPTION"
              />
            </div>
            <div className="col-4">
              <Select
                name="WRITE_ACCESS"
                label="Write Access"
                placeholder="Access"
                value={profile.WRITE_ACCESS}
                options={access}
                onChange={handleChange}
                error={errors.WRITE_ACCESS}
                listKey="ID"
                listName="DESCRIPTION"
              />
            </div>
          </div>
          <div className="row mt-4">
            <h4>Sites</h4>
          </div>
          <div className="row justify-content-center">
            <div className="col">
              <Checkbox
                name={"ACCESS_BA"}
                label="Bardney"
                checked={profile && profile.ACCESS_BA}
                onClick={handleChange}
                disabled={user && !user.ACCESS_BA}
              />
            </div>
            <div className="col">
              <Checkbox
                name={"ACCESS_BU"}
                label="Bury"
                checked={profile && profile.ACCESS_BU}
                onClick={handleChange}
                disabled={user && !user.ACCESS_BU}
              />
            </div>
            <div className="col">
              <Checkbox
                name={"ACCESS_CA"}
                label="Cantley"
                checked={profile && profile.ACCESS_CA}
                onClick={handleChange}
                disabled={user && !user.ACCESS_CA}
              />
            </div>
            <div className="col">
              <Checkbox
                name={"ACCESS_NE"}
                label="Newark"
                checked={profile && profile.ACCESS_NE}
                onClick={handleChange}
                disabled={user && !user.ACCESS_NE}
              />
            </div>
            <div className="col">
              <Checkbox
                name={"ACCESS_WI"}
                label="Wissington"
                checked={profile && profile.ACCESS_WI}
                onClick={handleChange}
                disabled={user && !user.ACCESS_WI}
              />
            </div>
            <div className="col">
              <Checkbox
                name={"ACCESS_CO"}
                label="Central Office"
                checked={profile && profile.ACCESS_CO}
                onClick={handleChange}
                disabled={user && !user.ACCESS_CO}
              />
            </div>
          </div>
          {match.params.id !== "new" && (
            <div className="row justify-content-center mt-4">
              <div className="col-3">
                <h4>Viewing</h4>
                <div className="mb-2">
                  <Checkbox
                    name="VIEW_DATA_SCREENS"
                    label="View Data Screens"
                    checked={profile && profile.VIEW_DATA_SCREENS}
                    onClick={handleChange}
                    disabled={user && !user.VIEW_DATA_SCREENS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="VIEW_DASHBOARDS"
                    label="View Dashboards"
                    checked={profile && profile.VIEW_DASHBOARDS}
                    onClick={handleChange}
                    disabled={user && !user.VIEW_DASHBOARDS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="REPORTS"
                    label="View Reports"
                    checked={profile && profile.REPORTS}
                    onClick={handleChange}
                    disabled={user && !user.REPORTS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="DATA_GRABBER"
                    label="View Grabbers"
                    checked={profile && profile.DATA_GRABBER}
                    onClick={handleChange}
                    disabled={user && !user.DATA_GRABBER}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="LIVE_DATA"
                    label="View Live Data"
                    checked={profile && profile.LIVE_DATA}
                    onClick={handleChange}
                    disabled={user && !user.LIVE_DATA}
                  />                  
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="VIEW_CCF"
                    label="View CCFs"
                    checked={profile && profile.VIEW_CCF}
                    onClick={handleChange}
                    disabled={user && !user.VIEW_CCF}
                  />
                </div>
              </div>
              <div className="col-3">
                <h4>Editing</h4>
                <div className="mb-2">
                  <Checkbox
                    name="MAINTAIN_DATA_SCREENS"
                    label="Edit Data Screens"
                    checked={profile && profile.MAINTAIN_DATA_SCREENS}
                    onClick={handleChange}
                    disabled={user && !user.MAINTAIN_DATA_SCREENS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="MAINTAIN_DASHBOARDS"
                    label="Edit Dashboards"
                    checked={profile && profile.MAINTAIN_DASHBOARDS}
                    onClick={handleChange}
                    disabled={user && !user.MAINTAIN_DASHBOARDS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="MAINTAIN_REPORTS"
                    label="Edit Reports"
                    checked={profile && profile.MAINTAIN_REPORTS}
                    onClick={handleChange}
                    disabled={user && !user.MAINTAIN_REPORTS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="MAINTAIN_GRABBERS"
                    label="Edit Data Grabbers"
                    checked={profile && profile.MAINTAIN_GRABBERS}
                    onClick={handleChange}
                    disabled={user && !user.MAINTAIN_GRABBERS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="PARAMETERS"
                    label="Edit Parameters"
                    checked={profile && profile.PARAMETERS}
                    onClick={handleChange}
                    disabled={user && !user.PARAMETERS}
                  />
                </div>
                <Checkbox
                  name="HISTORIC_DATA"
                  label="Edit Historic Data"
                  checked={profile && profile.HISTORIC_DATA}
                  onClick={handleChange}
                  disabled={user && !user.HISTORIC_DATA}
                />
              </div>
              <div className="col-3">
                <h4>Advanced</h4>
                <div className="mb-2">
                  <Checkbox
                    name="CONSTANTS"
                    label="Constants"
                    checked={profile && profile.CONSTANTS}
                    onClick={handleChange}
                    disabled={user && !user.CONSTANTS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="OPERATIONS"
                    label="Operations"
                    checked={profile && profile.OPERATIONS}
                    onClick={handleChange}
                    disabled={user && !user.OPERATIONS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="LOOKUPS"
                    label="Lookups"
                    checked={profile && profile.LOOKUPS}
                    onClick={handleChange}
                    disabled={user && !user.LOOKUPS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="USERS"
                    label="Users"
                    checked={profile && profile.USERS}
                    onClick={handleChange}
                    disabled={user && !user.USERS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="PRODUCTION_COMMENTS"
                    label="Comments"
                    checked={profile && profile.PRODUCTION_COMMENTS}
                    onClick={handleChange}
                    disabled={user && !user.PRODUCTION_COMMENTS}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="RECALCULATE"
                    label="Recalculate"
                    checked={profile && profile.RECALCULATE}
                    onClick={handleChange}
                    disabled={user && !user.RECALCULATE}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    name="MAINTAIN_CCF"
                    label="CCF Settings"
                    checked={profile && profile.MAINTAIN_CCF}
                    onClick={handleChange}
                    disabled={user && !user.MAINTAIN_CCF}
                  />
                </div>
              </div>
            </div>
          )}
          <button
            className="btn btn-primary mt-4"
            onClick={(e) => handleUpdate(e)}
            disabled={validate()}
          >
            {match.params.id === "new" ? "Create" : "Update"}
          </button>
        </form>
        {match.params.id !== "new" && profile && !profile.EMAIL && (
          <button
            className="btn btn-outline-danger btn-sm mt-3"
            onClick={handlePasswordReset}
          >
            Reset Password
          </button>
        )}
        {match.params.id !== "new" && (
          <div className="row mt-3">
            <div className="col">
              <Chart data={screenLog} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserForm;
